/**
 * 나라장터쇼핑몰 현황 (Phase 9-2) — DB 운영.
 *
 * 3개 탭 + 서브토글 (2026-07-19 주석 갱신):
 *   1) 등록현황        — MAS(g2b_mas_product) ↔ 3자단가(g2b_third_party_price) 서브토글, CRUD.
 *                        [📄 관급내역서] 엑셀 생성. MAS 식별번호 클릭=제품 사진 팝업.
 *   2) 정산요율        — g2b_settlement_rate (CRUD) + [📄 정산서] 엑셀 생성.
 *   3) BOM 원가 산정   — MAS(DB, 등록현황과 동일 원천) / 3자단가 좌측, 우측 구성부품 BOM.
 *                        구성은 g2b_bom_template(DB)에 영구 저장(자동 저장). 매입가는
 *                        [최근 매입가 갱신] 버튼으로 매입내역에서 재fetch. 마진=공급가 기준.
 *
 * 권한:
 *   - viewer       : 등록현황·정산요율 조회만 (수수료·요율 컬럼 hidden, BOM 탭 hidden, 편집·문서생성 버튼 hidden).
 *   - sales+       : 3개 탭 + 모든 컬럼 + CRUD + 문서 생성.
 */

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { VatNote } from '../components/VatNote';
import { SearchBar } from '../components/SearchBar';
import * as g2bApi from '../lib/g2b-api';
import type { MasProductRow as ApiMasProductRow } from '../lib/g2b-api';
import { fetchRecentSupplier, listItems } from '../lib/stock-api';
import type { ItemMasterRow } from '../lib/types';
import { PageTabs } from '../components/ui/PageTabs';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { applyColState, ColumnHeader, useColState, type Column } from '../components/DataTable';
import { PctInput } from '../components/g2b/PctInput';
import { BASIS_LABEL, shortWon, tiersFromText, volumeDiscountLabel, type VolumeDiscountBasis } from '../lib/g2b-discount';
import { LoadingLines } from '../components/ui/Skeleton';
import { RowMenu } from '../components/ui/RowMenu';

const G2bMasFormModal = lazy(() =>
  import('../components/G2bMasFormModal').then((m) => ({ default: m.G2bMasFormModal })),
);
const GwangeupModal = lazy(() =>
  import('../components/g2b/GwangeupModal').then((m) => ({ default: m.GwangeupModal })),
);
const SettlementModal = lazy(() =>
  import('../components/g2b/SettlementModal').then((m) => ({ default: m.SettlementModal })),
);

// ---------- 등록업체 마스터 ----------
const G2B_VENDORS = [
  '비알인포텍', '아이즈온솔루션', '파로스', '한국씨텍', '시큐인포',
  '인텔리빅스', '경림이앤지', '세오', '송우인포텍', '지인테크',
];

// ---------- BOM 항목 ----------
interface BomItem {
  itemCode: string;
  itemName: string;
  spec?: string;  // 품명&품목 마스터의 규격 (신규 추가 시 자동 채움, 기존은 lookup fallback)
  vendor: string; // = 매입처
  qty: number;
  recent: number; // 매입내역의 최근 단가 (자동 fetch)
  avg: number;    // 매입내역의 평균 단가 (자동 fetch)
  basis: 'recent' | 'avg';
  kind?: 'commission';  // '영업수수료' 특수행 — 원가 = 판매가 × 요율(%)
  ratePct?: number;     // 영업수수료 요율(%) — 정산요율에서 자동완성, 수정 가능
}

// 영업수수료(BOM) 특수 itemCode
const COMMISSION_CODE = '__SALES_COMMISSION__';
// BOM 한 줄의 원가 (영업수수료면 판매가×요율, 아니면 수량×기준단가)
function bomLineCost(it: BomItem, productPrice: number): number {
  if (it.kind === 'commission') return Math.round(productPrice * ((it.ratePct ?? 0) / 100));
  return it.qty * (it.basis === 'recent' ? it.recent : it.avg);
}

// ---------- 헬퍼 ----------
// MAS 헬퍼는 하드코딩 MasProduct 와 DB MasProductRow 둘 다 받도록 구조 타입 사용.
const fmt = (n: number) => n.toLocaleString('ko-KR');
const masPrice = (m: { priceText: string }) => parseInt(m.priceText.replace(/[^0-9]/g, ''), 10);
const masModel = (m: { fullName: string }) => m.fullName.split(',').slice(2, 3).join('').trim() || '—';
// BOM 저장 키 — MAS 는 동일 풀네임(가격만 다른 건: ES-650V3 5515+)이 있어 풀네임+가격으로 구분.
const masKey = (m: { fullName: string; priceText: string }) => `${m.fullName}||${m.priceText}`;

type G2bTab = 'reg' | 'rate' | 'bom';

export function G2BPage() {
  const { user } = useAuth();
  const showCost = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';

  const [tab, setTab] = useState<G2bTab>('reg');
  const [regSub, setRegSub] = useState<'mas' | 'tpp'>('mas');
  const [vendors, setVendors] = useState<string[]>(G2B_VENDORS);
  const [showGwangeup, setShowGwangeup] = useState(false);

  // 등록업체 마스터 = 기본 10곳 ∪ DB 3자단가 행에 실제 쓰인 업체 (2026-07-19 수리 —
  // 인라인 추가 업체가 새로고침 시 사라지던 문제: 행에 저장된 업체는 DB에서 자동 복원).
  const tppMasterQ = useQuery({ queryKey: ['g2b', 'tpp'], queryFn: g2bApi.listTpp, staleTime: 60_000 });
  useEffect(() => {
    const fromDb = new Set<string>();
    for (const t of tppMasterQ.data ?? []) for (const v of t.vendors) if (v) fromDb.add(v);
    if (fromDb.size === 0) return;
    setVendors((prev) => {
      const merged = [...prev];
      for (const v of fromDb) if (!merged.includes(v)) merged.push(v);
      return merged.length === prev.length ? prev : merged;
    });
  }, [tppMasterQ.data]);

  return (
    <div className="p-8">
      <div className="flex justify-between items-start mb-5">
        <div>
          <h1 className="ui-page-title">나라장터쇼핑몰 현황</h1>
          <p className="ui-page-sub flex items-center gap-2 flex-wrap">
            <span>shop.g2b.go.kr — SMSYSTEMS 등록 현황 및 BOM 원가 산정</span>
            <VatNote included />
          </p>
          <LastUpdateBadge className="mt-1" />
        </div>
        <a
          href="https://shop.g2b.go.kr/"
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
        >
          🔗 나라장터쇼핑몰
        </a>
      </div>

      <PageTabs
        tabs={[
          { key: 'reg', label: '등록현황' },
          { key: 'rate', label: '정산요율' },
          {
            key: 'bom',
            label: <>BOM 원가 산정 <span className="text-xs text-amber-600">🔒 영업부</span></>,
            hidden: !showCost,
          },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel="나라장터 보기"
        className="mb-6"
      />

      {tab === 'reg' && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <SegmentedControl
              ariaLabel="등록현황 구분"
              options={[
                { key: 'mas', label: 'MAS' },
                { key: 'tpp', label: '3자단가' },
              ]}
              value={regSub}
              onChange={setRegSub}
            />
            {canEdit && (
              <button
                type="button"
                onClick={() => setShowGwangeup(true)}
                className="btn btn-success-solid"
              >
                📄 관급내역서 작성
              </button>
            )}
          </div>
          {regSub === 'mas' && <MasTab />}
          {regSub === 'tpp' && <TppTab vendors={vendors} setVendors={setVendors} />}
        </div>
      )}
      {tab === 'rate' && <RateTab showCost={showCost} />}
      {tab === 'bom' && showCost && <BomTab />}
      {showGwangeup && (
        <Suspense fallback={null}>
          <GwangeupModal onClose={() => setShowGwangeup(false)} />
        </Suspense>
      )}
    </div>
  );
}


// =========================================================
// 1) MAS 제품 현황
// =========================================================
function MasTab() {
  const { user } = useAuth();
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const [filter, setFilter] = useState<'전체' | '서버' | '디스크어레이'>('전체');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiMasProductRow | null>(null);
  const [viewing, setViewing] = useState<ApiMasProductRow | null>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['g2b', 'mas'],
    queryFn: g2bApi.listMas,
    staleTime: 60_000,
  });
  const allRows = query.data ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => g2bApi.deleteMas(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['g2b', 'mas'] }),
    onError: (e: Error) => window.alert(`삭제 실패: ${e.message}`),
  });

  const filtered = useMemo(() => {
    return allRows.filter((m) => {
      if (filter !== '전체' && m.category !== filter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!`${m.fullName} ${m.g2bId ?? ''}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allRows, filter, search]);

  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const m of allRows) {
      if (m.g2bId) set.add(m.g2bId);
      if (m.fullName) {
        const head = m.fullName.split(',')[0]?.trim();
        if (head) set.add(head);
      }
    }
    return [...set];
  }, [allRows]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <SegmentedControl
          ariaLabel="품목 구분"
          options={(['전체', '서버', '디스크어레이'] as const).map((c) => ({ key: c, label: c }))}
          value={filter}
          onChange={setFilter}
        />
        <div className="flex items-center gap-2">
          <SearchBar
            value={search}
            onCommit={setSearch}
            placeholder="🔍 식별번호 / 모델 / 규격"
            className="w-80"
            live
            suggestions={searchSuggestions}
          />
          {canEdit && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn btn-primary"
            >
              + 제품 등록
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-center !bg-slate-700 !text-white">분류</th>
              <th className="text-left !bg-slate-700 !text-white">규격 (분류, 업체, 모델, 상세)</th>
              <th className="text-center !bg-slate-700 !text-white">식별번호</th>
              <th className="text-right !bg-slate-700 !text-white">금액</th>
              <th className="text-center !bg-slate-700 !text-white">납품기한</th>
              <th className="text-center !bg-slate-700 !text-white">계약종료일</th>
              <th className="text-left !bg-slate-700 !text-white">비고</th>
              {canEdit && <th className="text-center !bg-slate-700 !text-white w-24">관리</th>}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-slate-400 py-8"><LoadingLines /></td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-red-600 py-8">오류: {(query.error as Error).message}</td></tr>
            )}
            {!query.isLoading && !query.isError && filtered.length === 0 && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-slate-400 py-8">등록된 제품이 없습니다</td></tr>
            )}
            {filtered.map((m) => (
              <tr key={m.id}>
                <td className="text-center">
                  <span className={`stage-badge ${m.category === '서버' ? 'stage-order' : 'stage-deliv'}`}>
                    {m.category}
                  </span>
                </td>
                <td className="text-xs">{m.fullName}</td>
                <td className="text-center font-mono text-xs">
                  {m.hasImage ? (
                    <button
                      type="button"
                      onClick={() => setViewing(m)}
                      title="제품 사진 보기 (다운로드·복사)"
                      className="text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      <span aria-hidden>🖼</span>
                      {m.g2bId ?? '사진'}
                    </button>
                  ) : canEdit ? (
                    <button
                      type="button"
                      onClick={() => setViewing(m)}
                      title="제품 사진 추가"
                      className="text-slate-500 hover:underline inline-flex items-center gap-1"
                    >
                      {m.g2bId ?? <span className="text-amber-600 text-[11px]">⚠ 미확인</span>}
                      <span className="text-[11px] text-slate-500">＋사진</span>
                    </button>
                  ) : m.g2bId ? (
                    <a href="https://shop.g2b.go.kr/" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:underline">
                      {m.g2bId}
                    </a>
                  ) : (
                    <span className="text-amber-600 text-[11px]">⚠ 미확인</span>
                  )}
                </td>
                <td className="text-right num font-semibold">{m.priceText}</td>
                <td className="text-center text-xs">{m.deliveryDays}</td>
                <td className="text-center text-xs">{m.endDate}</td>
                <td className="text-xs text-amber-700">{m.note ?? ''}</td>
                {canEdit && (
                  <td className="text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        type="button"
                        onClick={() => setEditing(m)}
                        title="수정"
                        className="btn btn-secondary btn-sm"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`이 항목을 삭제하시겠습니까?\n\n${m.fullName.slice(0, 60)}…`)) {
                            deleteMut.mutate(m.id);
                          }
                        }}
                        disabled={deleteMut.isPending}
                        title="삭제"
                        className="btn btn-danger btn-sm"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3">
        💡 데이터 출처: 나라장터쇼핑몰 → "에스엠시스템즈" 검색. 행 클릭 또는 [✏️] 으로 수정, [🗑] 으로 삭제.
        <br />
        🖼 <b>식별번호</b>를 클릭하면 제품 사진을 크게 보고 <b>다운로드</b>하거나 <b>복사</b>(붙여넣기)할 수 있습니다.
      </p>

      {viewing && <MasImageModal product={viewing} canEdit={canEdit} onClose={() => setViewing(null)} />}

      <Suspense fallback={null}>
        {creating && (
          <G2bMasFormModal
            mode="create"
            onClose={() => setCreating(false)}
            onSaved={() => setCreating(false)}
          />
        )}
        {editing && (
          <G2bMasFormModal
            mode="edit"
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => setEditing(null)}
          />
        )}
      </Suspense>
    </div>
  );
}

// ── MAS 제품 사진 팝업 (식별번호 클릭) ──────────────────
/** 사진 팝업이 다루는 대상 (MAS 제품 / 3자단가 항목 공용) */
interface PhotoTarget {
  id: number;
  /** 굵게 표시할 짧은 이름(모델) */
  model: string;
  /** 그 아래 회색으로 표시할 전체 규격명 */
  fullName: string;
  g2bId: string | null;
  hasImage: boolean;
}

/** 사진 팝업이 쓸 API 묶음 — 메뉴별로 엔드포인트·목록 캐시키·팝업 폭만 다르다. */
interface PhotoApi {
  url: (id: number, bust?: number | string) => string;
  upload: (id: number, file: File | Blob) => Promise<void>;
  remove: (id: number) => Promise<void>;
  queryKey: string[];
  /** 사진이 뜨기 전 쓰는 기본 팝업 폭(= 그 메뉴에서 가장 넓은 사진 + 좌우 패딩 74).
   *  사진이 로드되면 실제 원본 폭에 맞춰 다시 좁혀진다(ProductImageModal 참고). */
  width: number;
  hint: string;
}

/** 사진 좌우로 들어가는 여백(팝업 padding 24×2 + 사진칸 padding 12×2 + 테두리 2). */
const PHOTO_CHROME_W = 74;
/** 세로가 길어 창 안에 스크롤바가 생기는 사진은 그만큼 폭을 더 준다(안 주면 1:1 이 깨진다). */
const PHOTO_SCROLLBAR_W = 16;

/**
 * 제품 사진 팝업 — MAS 제품현황과 3자단가 현황이 **같은 컴포넌트**를 쓴다 (2026-07-29).
 *   메뉴별로 다른 것은 PhotoApi(엔드포인트·캐시키·폭)뿐이라 화면 동작이 갈라지지 않는다.
 */
function ProductImageModal({
  target,
  api,
  canEdit,
  onClose,
}: {
  target: PhotoTarget;
  api: PhotoApi;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // 교체 직후 <img> 캐시 무효화용 버전. 사진 유무는 로컬 상태로 추적(업로드/삭제 즉시 반영).
  const [ver, setVer] = useState(0);
  const [hasImg, setHasImg] = useState(target.hasImage);
  const [busy, setBusy] = useState<'' | 'copy' | 'download'>('');
  const [msg, setMsg] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);
  // 사진 원본 크기(로드 후). 팝업을 사진 폭에 맞춰 좁혀 양옆 빈 공간을 없앤다.
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const width = !hasImg
    ? 560 // 사진이 없으면 안내문만 뜨므로 넓은 창이 필요 없다
    : nat
      ? Math.min(
          api.width,
          Math.max(560, nat.w + PHOTO_CHROME_W + (nat.h > 420 ? PHOTO_SCROLLBAR_W : 0)),
        )
      : api.width;

  const model = target.model;
  const baseName = (target.g2bId || model).replace(/[\\/:*?"<>|]/g, '_');
  const src = api.url(target.id, ver);

  async function fetchPngBlob(): Promise<Blob> {
    const res = await fetch(src, { credentials: 'include' });
    if (!res.ok) throw new Error('사진을 불러오지 못했습니다');
    const blob = await res.blob();
    if (blob.type === 'image/png') return blob;
    // 다른 형식은 PNG 로 변환(클립보드 호환)
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    canvas.getContext('2d')!.drawImage(bmp, 0, 0);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('변환 실패'))), 'image/png'),
    );
  }

  async function onDownload() {
    setBusy('download'); setMsg('');
    try {
      const blob = await fetchPngBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseName}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setMsg((e as Error).message || '다운로드 실패');
    } finally {
      setBusy('');
    }
  }

  async function onCopy() {
    setBusy('copy'); setMsg('');
    try {
      const blob = await fetchPngBlob();
      if (!navigator.clipboard || typeof window.ClipboardItem === 'undefined') {
        throw new Error('이 브라우저는 이미지 복사를 지원하지 않습니다. 다운로드를 이용하세요.');
      }
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      setMsg('✓ 복사됨 — 문서·메일에 붙여넣기(Ctrl+V) 하세요');
    } catch (e) {
      setMsg((e as Error).message || '복사 실패');
    } finally {
      setBusy('');
    }
  }

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.upload(target.id, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: api.queryKey });
      setHasImg(true);
      setVer((v) => v + 1);
      setMsg(hasImg ? '✓ 사진을 교체했습니다' : '✓ 사진을 등록했습니다');
    },
    onError: (e: Error) => setMsg(e.message || '업로드 실패'),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.remove(target.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: api.queryKey });
      onClose();
    },
    onError: (e: Error) => setMsg(e.message || '삭제 실패'),
  });

  return (
    <ModalShell title="제품 사진" onClose={onClose} width={width}>
      <div className="p-6 space-y-4">
        <div className="text-xs text-slate-600 leading-relaxed">
          <div className="font-medium text-slate-800">{model}</div>
          <div className="text-slate-500">{target.fullName}</div>
          {target.g2bId && (
            <div className="mt-1 font-mono">
              식별번호 {target.g2bId}{' '}
              <a href="https://shop.g2b.go.kr/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">🔗 나라장터</a>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center bg-slate-50 border border-slate-200 rounded p-3 min-h-[140px]">
          {hasImg ? (
            // 세로가 긴 캡쳐도 1:1 로 보여준다(줄이면 글자를 못 읽는다). 길면 창 안에서 세로 스크롤.
            <img
              src={src}
              alt={model}
              onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              className="max-w-full h-auto object-contain"
            />
          ) : (
            <div className="text-sm text-slate-400 py-8">등록된 사진이 없습니다</div>
          )}
        </div>

        {msg && <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">{msg}</div>}

        <div className="flex flex-wrap gap-2">
          {hasImg && (
            <>
              <button
                type="button"
                onClick={onDownload}
                disabled={busy !== ''}
                className="btn btn-primary"
              >
                {busy === 'download' ? '준비 중…' : '⬇ 다운로드'}
              </button>
              <button
                type="button"
                onClick={onCopy}
                disabled={busy !== ''}
                className="btn btn-outline"
              >
                {busy === 'copy' ? '복사 중…' : '📋 복사'}
              </button>
            </>
          )}
          {canEdit && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadMut.mutate(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadMut.isPending}
                className="btn btn-secondary ml-auto"
              >
                {uploadMut.isPending ? '올리는 중…' : hasImg ? '🖼 사진 교체' : '🖼 사진 등록'}
              </button>
              {hasImg && (
                <button
                  type="button"
                  onClick={() => { if (window.confirm('이 제품의 사진을 삭제하시겠습니까?')) deleteMut.mutate(); }}
                  disabled={deleteMut.isPending}
                  className="btn btn-danger"
                >
                  🗑 삭제
                </button>
              )}
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">{api.hint}</p>
      </div>
    </ModalShell>
  );
}

/** MAS 제품사진 팝업 설정 — 사진 1512px(2026-07-29 재시드) + 좌우 패딩 74 = 1590 에서 1:1. */
const MAS_PHOTO_API: PhotoApi = {
  url: g2bApi.masImageUrl,
  upload: g2bApi.uploadMasImage,
  remove: g2bApi.deleteMasImage,
  queryKey: ['g2b', 'mas'],
  width: 1590,
  hint: '※ 나라장터쇼핑몰 캡쳐에서 자른 사진이라 작을 수 있습니다. 관리자는 [사진 교체]로 고화질 이미지를 올릴 수 있습니다.',
};

/** 3자단가 사진 팝업 설정 (2026-07-29) — 주품목 캡쳐가 1373px 로 가장 넓다(+패딩 74 = 1447).
 *  선택부품은 상품 상세 캡쳐라 세로가 길다(1188×1187) → 팝업이 1262 로 좁아지고 세로 스크롤. */
const TPP_PHOTO_API: PhotoApi = {
  url: g2bApi.tppImageUrl,
  upload: g2bApi.uploadTppImage,
  remove: g2bApi.deleteTppImage,
  queryKey: ['g2b', 'tpp'],
  width: 1460,
  hint: '※ 나라장터쇼핑몰 상품 화면을 그대로 캡쳐한 사진입니다. 사진이 길면 창 안에서 아래로 스크롤하세요.',
};

/** MAS 제품 사진 팝업 — 모델명은 풀네임의 3번째 토막(예: "…, 에스엠시스템즈, ES-650V3, …"). */
function MasImageModal({
  product,
  canEdit,
  onClose,
}: {
  product: ApiMasProductRow;
  canEdit: boolean;
  onClose: () => void;
}) {
  const model = product.fullName.split(',').slice(2, 3).join('').trim() || 'product';
  return (
    <ProductImageModal
      target={{
        id: product.id,
        model,
        fullName: product.fullName,
        g2bId: product.g2bId,
        hasImage: product.hasImage,
      }}
      api={MAS_PHOTO_API}
      canEdit={canEdit}
      onClose={onClose}
    />
  );
}

/** 3자단가 제품 사진 팝업 — 모델명은 규격명의 3번째 토막(예: "영상감시장치, 비알인포텍, BR-S2010, …"). */
function TppImageModal({
  item,
  canEdit,
  onClose,
}: {
  item: g2bApi.TppRow;
  canEdit: boolean;
  onClose: () => void;
}) {
  const model =
    item.specText.split(',').slice(2, 3).join('').trim() ||
    item.specText.split(',')[0]?.trim() ||
    'product';
  return (
    <ProductImageModal
      target={{
        id: item.id,
        model,
        fullName: item.specText,
        g2bId: item.g2bId,
        hasImage: item.hasImage,
      }}
      api={TPP_PHOTO_API}
      canEdit={canEdit}
      onClose={onClose}
    />
  );
}

// =========================================================
// 2) 3자단가 현황 (DB CRUD, 2026-05-18 본 운영)
// =========================================================
function TppTab({
  vendors,
  setVendors,
}: {
  vendors: string[];
  setVendors: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const [filter, setFilter] = useState<'전체' | '선택부품' | '주품목'>('전체');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<g2bApi.TppRow | null>(null);
  // 식별번호 클릭 → 제품 사진 팝업 (2026-07-29, MAS 와 동일 UX)
  const [viewing, setViewing] = useState<g2bApi.TppRow | null>(null);
  const qc = useQueryClient();

  // 마스터 영역에서 신규 업체 인라인 입력
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const addVendorFromMaster = () => {
    const name = newVendorName.trim();
    if (!name || vendors.includes(name)) {
      setNewVendorName('');
      setShowAddVendor(false);
      return;
    }
    setVendors([...vendors, name]);
    setNewVendorName('');
    setShowAddVendor(false);
  };

  const query = useQuery({
    queryKey: ['g2b', 'tpp'],
    queryFn: g2bApi.listTpp,
    staleTime: 60_000,
  });
  const allRows = query.data ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => g2bApi.deleteTpp(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['g2b', 'tpp'] }),
    onError: (e: Error) => window.alert(`삭제 실패: ${e.message}`),
  });

  const filtered = useMemo(() => {
    return allRows.filter((t) => {
      if (filter !== '전체' && t.ctype !== filter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!`${t.specText} ${t.g2bId} ${t.vendors.join(' ')}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allRows, filter, search]);

  const totalRegistrations = filtered.reduce((a, t) => a + t.vendors.length, 0);

  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const t of allRows) {
      if (t.g2bId) set.add(t.g2bId);
      if (t.specText) {
        const head = t.specText.split(',')[0]?.trim();
        if (head) set.add(head);
      }
      for (const v of t.vendors) if (v) set.add(v);
    }
    return [...set];
  }, [allRows]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <SegmentedControl
          ariaLabel="품목 구분"
          options={(['전체', '선택부품', '주품목'] as const).map((c) => ({ key: c, label: c }))}
          value={filter}
          onChange={setFilter}
        />
        <div className="flex gap-2">
          <SearchBar
            value={search}
            onCommit={setSearch}
            placeholder="🔍 식별번호 / 모델 / 업체명"
            className="w-80"
            live
            suggestions={searchSuggestions}
          />
          {canEdit && (
            <button type="button" onClick={() => setCreating(true)} className="btn btn-primary">
              + 제품 등록
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-center !bg-slate-700 !text-white">계약형태</th>
              <th className="text-left !bg-slate-700 !text-white">분류</th>
              <th className="text-left !bg-slate-700 !text-white">규격</th>
              <th className="text-center !bg-slate-700 !text-white">식별번호</th>
              <th className="text-right !bg-slate-700 !text-white">금액</th>
              <th className="text-left !bg-slate-700 !text-white" style={{ minWidth: '280px' }}>
                등록업체 <span className="text-xs text-slate-400 font-normal">({totalRegistrations} 등록)</span>
              </th>
              <th className="text-left !bg-slate-700 !text-white">비고</th>
              {canEdit && <th className="text-center !bg-slate-700 !text-white w-24">관리</th>}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-slate-400 py-8"><LoadingLines /></td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-red-600 py-8">오류: {(query.error as Error).message}</td></tr>
            )}
            {!query.isLoading && !query.isError && filtered.length === 0 && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-slate-400 py-8">등록된 제품이 없습니다</td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id}>
                <td className="text-center">
                  <span className={`stage-badge ${t.ctype === '주품목' ? 'stage-issue' : 'stage-quote'}`}>{t.ctype}</span>
                </td>
                <td className="text-xs text-slate-600">{t.cat}</td>
                <td className="text-xs">{t.specText}</td>
                {/* 식별번호 — 사진이 있으면 🖼 클릭으로 팝업, 없으면 관리자에게 ＋사진 (MAS 와 동일) */}
                <td className="text-center font-mono text-xs whitespace-nowrap">
                  {t.hasImage ? (
                    <button
                      type="button"
                      onClick={() => setViewing(t)}
                      title="제품 사진 보기 (다운로드·복사)"
                      className="text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      <span aria-hidden>🖼</span>
                      {t.g2bId}
                    </button>
                  ) : canEdit ? (
                    <button
                      type="button"
                      onClick={() => setViewing(t)}
                      title="제품 사진 추가"
                      className="text-slate-500 hover:underline inline-flex items-center gap-1"
                    >
                      {t.g2bId}
                      <span className="text-[11px] text-slate-500">＋사진</span>
                    </button>
                  ) : (
                    t.g2bId
                  )}
                </td>
                <td className="text-right num font-semibold">{fmt(t.price)}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {t.vendors.map((v) => (
                      <span key={v} className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200">{v}</span>
                    ))}
                  </div>
                </td>
                <td className="text-xs text-slate-600">{t.note || <span className="text-slate-300">-</span>}</td>
                {canEdit && (
                  <td className="text-center">
                    <div className="flex gap-1 justify-center">
                      <button type="button" onClick={() => setEditing(t)} title="수정"
                        className="btn btn-secondary btn-sm">
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`이 항목을 삭제하시겠습니까?\n\n${t.specText.slice(0, 60)}…`)) {
                            deleteMut.mutate(t.id);
                          }
                        }}
                        disabled={deleteMut.isPending}
                        title="삭제"
                        className="btn btn-danger btn-sm"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 mt-3">
        🖼 <b>식별번호</b>를 클릭하면 제품 사진을 크게 보고 <b>다운로드</b>하거나 <b>복사</b>(붙여넣기)할 수 있습니다.
      </p>

      {viewing && <TppImageModal item={viewing} canEdit={canEdit} onClose={() => setViewing(null)} />}

      <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
        <strong className="text-slate-900">등록업체 마스터 ({vendors.length}곳)</strong>
        <div className="flex flex-wrap gap-1.5 mt-2 items-center">
          {vendors.map((v) => {
            const cnt = allRows.filter((t) => t.vendors.includes(v)).length;
            return (
              <span key={v} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded">
                <span>{v}</span>
                <span className="text-slate-400">{cnt}</span>
              </span>
            );
          })}
          {showAddVendor ? (
            <span className="inline-flex items-center gap-1 px-1 py-0.5 bg-white border border-blue-300 rounded">
              <input
                autoFocus
                value={newVendorName}
                onChange={(e) => setNewVendorName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addVendorFromMaster(); }
                  else if (e.key === 'Escape') { setShowAddVendor(false); setNewVendorName(''); }
                }}
                placeholder="업체명"
                className="border-0 outline-none px-1 py-0.5 text-xs w-32"
              />
              <button type="button" onClick={addVendorFromMaster} className="text-blue-600 px-1 hover:bg-blue-50 rounded" title="추가 (Enter)">✓</button>
              <button type="button" onClick={() => { setShowAddVendor(false); setNewVendorName(''); }} className="text-slate-400 px-1 hover:bg-slate-50 rounded" title="취소 (Esc)">×</button>
            </span>
          ) : canEdit ? (
            <button type="button" onClick={() => setShowAddVendor(true)} className="btn btn-ghost btn-sm">+ 신규 업체</button>
          ) : null}
        </div>
      </div>

      {creating && (
        <TppFormModal
          mode="register"
          initial={null}
          onClose={() => setCreating(false)}
          vendors={vendors}
          setVendors={setVendors}
        />
      )}
      {editing && (
        <TppFormModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          vendors={vendors}
          setVendors={setVendors}
        />
      )}
    </div>
  );
}

function TppFormModal({
  mode,
  initial,
  onClose,
  vendors,
  setVendors,
}: {
  mode: 'register' | 'edit';
  initial: g2bApi.TppRow | null;
  onClose: () => void;
  vendors: string[];
  setVendors: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const title = mode === 'register' ? '3자단가 제품 등록' : '3자단가 제품 수정';
  const qc = useQueryClient();
  const [ctype, setCtype] = useState<'선택부품' | '주품목'>(initial?.ctype ?? '선택부품');
  const [cat, setCat] = useState<string>(initial?.cat ?? '하드디스크드라이브');
  const [g2bId, setG2bId] = useState<string>(initial?.g2bId ?? '');
  const [specText, setSpecText] = useState<string>(initial?.specText ?? '');
  const [note, setNote] = useState<string>(initial?.note ?? '');
  const [price, setPrice] = useState<number>(initial?.price ?? 0);
  const [sortOrder, setSortOrder] = useState<number>(initial?.sortOrder ?? 999);

  const [selected, setSelected] = useState<Set<string>>(new Set(initial?.vendors ?? []));
  const [newVendorInput, setNewVendorInput] = useState('');

  const toggleVendor = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    setSelected(next);
  };
  const addNewVendor = () => {
    const name = newVendorInput.trim();
    if (!name) return;
    if (!vendors.includes(name)) setVendors([...vendors, name]);
    const next = new Set(selected);
    next.add(name);
    setSelected(next);
    setNewVendorInput('');
  };

  const createMut = useMutation({
    mutationFn: (input: g2bApi.TppInput) => g2bApi.createTpp(input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['g2b', 'tpp'] }); onClose(); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<g2bApi.TppInput> }) => g2bApi.updateTpp(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['g2b', 'tpp'] }); onClose(); },
  });

  function onSubmit() {
    if (!specText.trim()) { window.alert('규격을 입력하세요'); return; }
    if (!g2bId.trim()) { window.alert('식별번호를 입력하세요'); return; }
    // ⭐2026-08-31: 화면 검사가 규격·식별번호 둘뿐이라 「분류」를 비우면 서버가 거절하고,
    //   「금액」을 비우면 **0원으로 조용히 저장**됐다 (운영 id=34 가 실제로 0원으로 들어갔다).
    if (!cat.trim()) { window.alert('분류를 입력하세요 (검수명판 품명으로 쓰입니다)'); return; }
    if (!price || price <= 0) { window.alert('금액을 입력하세요 (0원으로는 저장할 수 없습니다)'); return; }
    const input: g2bApi.TppInput = {
      ctype,
      cat: cat.trim(),
      specText: specText.trim(),
      g2bId: g2bId.trim(),
      price: price || 0,
      vendors: Array.from(selected),
      note: note.trim() || null,
      sortOrder: sortOrder || 999,
    };
    if (mode === 'edit' && initial) updateMut.mutate({ id: initial.id, input });
    else createMut.mutate(input);
  }
  const saving = createMut.isPending || updateMut.isPending;
  const error = createMut.error ?? updateMut.error;

  return (
    <ModalShell title={title} onClose={onClose}>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <label className="ui-label">계약형태 *</label>
            <select value={ctype} onChange={(e) => setCtype(e.target.value as '선택부품' | '주품목')} className="ui-input w-full mt-1">
              <option value="선택부품">선택부품</option>
              <option value="주품목">주품목</option>
            </select>
          </div>
          <div>
            <label className="ui-label">분류 *</label>
            <input value={cat} onChange={(e) => setCat(e.target.value)} className="ui-input w-full mt-1" placeholder="하드디스크드라이브 / 디스크어레이 / 서버 등" />
          </div>
          <div>
            <label className="ui-label">식별번호 *</label>
            <input value={g2bId} onChange={(e) => setG2bId(e.target.value)} className="ui-input w-full mt-1 font-mono" placeholder="예: 25227571" />
          </div>
        </div>
        <div>
          <label className="ui-label">규격 *</label>
          <textarea value={specText} onChange={(e) => setSpecText(e.target.value)} rows={2} className="ui-input ui-textarea w-full mt-1" placeholder="예: 에스엠시스템즈 US316D, 기본장착 480TB 32GB(16TB * 30)" />
        </div>
        <div>
          <label className="ui-label">비고</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="ui-input w-full mt-1" placeholder="옵션·특이사항 등 (선택)" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="ui-label">금액 (원) *</label>
            <input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value) || 0)} className="ui-input w-full mt-1 num" />
          </div>
          <div>
            <label className="ui-label">정렬 순서</label>
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 999)} className="ui-input w-full mt-1 num" />
          </div>
        </div>
        <div>
          <label className="ui-label">
            등록업체 (해당 업체 모두 선택) — 선택됨 {selected.size}곳
          </label>
          <div className="grid grid-cols-3 gap-2 mt-1 p-3 border border-slate-200 rounded">
            {vendors.map((v) => (
              <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selected.has(v)} onChange={() => toggleVendor(v)} />
                <span>{v}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <input
              value={newVendorInput}
              onChange={(e) => setNewVendorInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewVendor(); } }}
              placeholder="목록에 없는 새 업체명 입력 후 Enter (또는 [+ 추가])"
              className="ui-input flex-1"
            />
            <button type="button" onClick={addNewVendor} disabled={!newVendorInput.trim()}
              className="btn btn-secondary">
              + 추가
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            추가한 업체는 자동으로 체크됩니다. 마스터에도 추가됨.
          </p>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">
            {(error as Error).message}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
        <button type="button" onClick={onClose} className="btn btn-secondary">취소</button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? '저장 중…' : mode === 'register' ? '등록' : '수정'}
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================
// 3) 정산요율 (DB CRUD, 2026-05-18 본 운영)
// =========================================================
const rateKindClass = (k: string) =>
  k === 'MAS' ? 'stage-likely' : k === '주품목' ? 'stage-issue' : k === '선택부품' ? 'stage-quote' : 'stage-order';
/** 요율(소수) → 목록에 보일 % 글자 — 둘째 자리까지(저장은 더 정밀하게 한다 · v2.286.0) */
const ratePctLabel = (rate: number) => `${(rate * 100).toFixed(2)}%`;

type RateColumn = Column<g2bApi.RateRow> & { tdClass?: string };

function RateTab({ showCost }: { showCost: boolean }) {
  const { user } = useAuth();
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<g2bApi.RateRow | null>(null);
  const [showSettlement, setShowSettlement] = useState(false);

  const query = useQuery({
    queryKey: ['g2b', 'rate'],
    queryFn: g2bApi.listRate,
    staleTime: 60_000,
  });
  const rows = query.data ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => g2bApi.deleteRate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['g2b', 'rate'] }),
    onError: (e: Error) => window.alert(`삭제 실패: ${e.message}`),
  });
  const deleteRow = deleteMut.mutate;
  const deleting = deleteMut.isPending;

  // 머리줄 칸마다 정렬(▲▼)·🔍 걸러 보기 (v2.286.0 · 개발요청 #8). 정렬을 안 걸면 정렬 순서 칸 순서 그대로.
  // 조회 전용(viewer)은 수수료·요율 칸이 아예 없다 — 걸러 보기 목록에도 안 나온다(서버도 null 로 가린다).
  const colState = useColState({ storageKey: 'g2b-rate' });
  const columns = useMemo<RateColumn[]>(() => [
    {
      key: 'kind', label: '구분', type: 'text', align: 'center', nowrapHeader: true, tdClass: 'whitespace-nowrap',
      accessor: (r) => r.kind,
      render: (r) => <span className={`stage-badge ${rateKindClass(r.kind)}`}>{r.kind}</span>,
    },
    {
      // 유동 칸 — 다른 칸이 늘어도 찌그러지지 않게 하한(min-w)을 준다
      key: 'spec', label: '규격', type: 'text', tdClass: 'min-w-[240px]',
      accessor: (r) => r.spec,
      render: (r) => (
        <span className="text-xs text-slate-600" title={r.spec}>{r.spec.length > 80 ? r.spec.slice(0, 80) + '...' : r.spec}</span>
      ),
    },
    {
      key: 'price', label: '판매금액', type: 'number', align: 'right', nowrapHeader: true, tdClass: 'whitespace-nowrap',
      accessor: (r) => r.price,
      // 다량납품할인 기준 (v2.315.0) — 판매금액 아래 작은 글씨로. 따로 칸을 두면 1280 화면에서 표가 85px 넘쳤다(다른 칸은 이미 최소 폭 · 10-30 실측).
      //   정산서 작성 창이 이 기준으로 줄마다 할인율을 채운다 · 나라장터 공개 조건이라 조회 전용도 본다
      render: (r) => (
        <>
          <span className="num">{fmt(r.price)}</span>
          {r.volumeDiscount && (
            <div className="text-[11px] text-rose-700 leading-snug" title={`다량납품할인 기준 — ${volumeDiscountLabel(r.volumeDiscount)}`} data-vd-label>
              {[...r.volumeDiscount.tiers].sort((a, b) => a.min - b.min).map((t) => (
                <div key={t.min}>🏷 {r.volumeDiscount!.basis === 'qty' ? `${t.min.toLocaleString('ko-KR')}개` : shortWon(t.min)}↑ {t.pct}%</div>
              ))}
            </div>
          )}
        </>
      ),
    },
    ...(showCost
      ? ([
          {
            key: 'fee', label: '수수료', type: 'number', align: 'right', nowrapHeader: true,
            tdClass: 'whitespace-nowrap bg-amber-50/40',
            accessor: (r) => r.fee,
            render: (r) => (r.fee != null
              ? <span className="num" title={r.feeFixed ? '직접 적은 수수료 — 판매금액을 바꿔도 그대로' : '판매금액 × 요율'}>{fmt(r.fee)}</span>
              : <span className="text-slate-300">-</span>),
          },
          {
            key: 'rate', label: '요율', type: 'number', align: 'right', nowrapHeader: true,
            tdClass: 'whitespace-nowrap bg-amber-50/40',
            // 걸러 보기 목록은 화면에 보이는 둘째 자리 값으로 묶는다
            accessor: (r) => (r.rate != null ? +(r.rate * 100).toFixed(2) : null),
            render: (r) => (r.rate != null
              ? <span className="num">{ratePctLabel(r.rate)}</span>
              : <span className="text-slate-300">-</span>),
          },
        ] satisfies RateColumn[])
      : []),
    {
      key: 'vendor', label: '업체', type: 'text', tdClass: 'min-w-[110px]',
      accessor: (r) => r.vendor,
      // 3자단가 업체 목록은 길다 — 폭 상한(안쪽 max-w)을 둬서 규격 칸 자리를 뺏지 않게(1920·1536·1366·1280 실측)
      render: (r) => <div className="text-xs max-w-[260px]">{r.vendor}</div>,
    },
    // 비고 — 예전엔 수정 창에서만 보였다(v2.286.0 목록에 추가). 요율(「30.09027%」)·견적가가 적혀 있어
    // 수수료·요율처럼 조회 전용에게는 안 보인다(서버도 null).
    ...(showCost
      ? ([
          {
            key: 'note', label: '비고', type: 'text', tdClass: 'min-w-[120px]',
            accessor: (r) => r.note ?? '',
            render: (r) => (r.note ? <div className="text-xs text-slate-600 whitespace-pre-line max-w-[220px]">{r.note}</div> : null),
          },
        ] satisfies RateColumn[])
      : []),
    ...(canEdit
      ? ([
          {
            key: 'actions', label: '', type: 'text', align: 'center', noSort: true, noFilter: true, nowrapHeader: true,
            tdClass: 'whitespace-nowrap w-10',
            accessor: () => '',
            // v2.317.0 · 줄 끝 ▼ 메뉴 한 칸(Salesforce 식) — 전에는 [✏️][🗑] 두 단추가 칸 하나를 차지했다
            render: (r) => (
              <RowMenu
                items={[
                  { label: '수정', onClick: () => setEditing(r) },
                  {
                    label: '삭제',
                    tone: 'danger',
                    disabled: deleting,
                    onClick: () => {
                      if (window.confirm(`이 항목을 삭제하시겠습니까?\n\n${r.spec.slice(0, 60)}…`)) {
                        deleteRow(r.id);
                      }
                    },
                  },
                ]}
              />
            ),
          },
        ] satisfies RateColumn[])
      : []),
  ], [showCost, canEdit, deleteRow, deleting]);
  const visibleRows = useMemo(() => applyColState(rows, columns, colState), [rows, columns, colState]);
  const filtering = Object.keys(colState.filters).length > 0 || colState.sortStack.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
          <span>
            정산요율 통합 목록 ({visibleRows.length === rows.length ? `${rows.length}건` : `${visibleRows.length} / ${rows.length}건`}).
            {canEdit ? ' 행을 눌러 수정 · 머리줄을 눌러 정렬 · 🔍 로 걸러 보기.' : ' 머리줄을 눌러 정렬 · 🔍 로 걸러 보기.'}
          </span>
          {filtering && (
            <button type="button" onClick={colState.resetAll} className="btn btn-secondary btn-sm">↺ 필터·정렬 초기화</button>
          )}
        </p>
        {canEdit && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowSettlement(true)}
              className="btn btn-success-solid"
            >
              📄 정산서 작성
            </button>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn btn-primary"
            >
              + 항목 추가
            </button>
          </div>
        )}
      </div>
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]" data-rate-table>
          <thead>
            <tr>
              {columns.map((col) => (
                <ColumnHeader key={col.key} column={col} baseRows={rows} columns={columns} colState={colState} />
              ))}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={columns.length} className="text-center text-slate-400 py-8"><LoadingLines /></td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={columns.length} className="text-center text-red-600 py-8">오류: {(query.error as Error).message}</td></tr>
            )}
            {!query.isLoading && !query.isError && visibleRows.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center text-slate-400 py-8">
                {rows.length > 0 ? '조건에 맞는 항목이 없습니다 — [↺ 필터·정렬 초기화] 로 되돌릴 수 있습니다.' : '등록된 항목이 없습니다'}
              </td></tr>
            )}
            {visibleRows.map((r) => (
              <tr key={r.id} className={canEdit ? 'cursor-pointer hover:bg-slate-50' : ''} onClick={() => { if (canEdit) setEditing(r); }}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.tdClass ?? ''}`}
                  >
                    {col.render ? col.render(r) : (col.accessor(r) ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <RateFormModal mode="create" initial={null} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <RateFormModal mode="edit" initial={editing} onClose={() => setEditing(null)} />
      )}
      {showSettlement && (
        <Suspense fallback={null}>
          <SettlementModal onClose={() => setShowSettlement(false)} />
        </Suspense>
      )}
    </div>
  );
}

/** 요율(소수) → 수정 창 % 글자 — 저장된 자리 그대로(여덟째 자리까지 · 꼬리 0 없이) */
const ratePctText = (rate: number) => String(+(rate * 100).toFixed(8));

/**
 * 저장된 줄을 열 때 무엇이 기준인가.
 *   · 수수료를 직접 적은 줄(feeFixed) → 수수료
 *   · v2.286.0 전에 저장된 줄 중 수수료가 「판매금액 × 요율」과 어긋난 줄 → 사람이 맞춰 둔 금액이다(운영 id 11·13 —
 *     요율을 25.384615% 로 적어 9,900,000원을 만들었는데 요율만 25.38% 로 깎였다) → 수수료를 지킨다
 *   · 그 밖 → 요율(예전 그대로 · 판매금액을 바꾸면 수수료가 따라간다)
 */
function initialFeeAnchor(r: g2bApi.RateRow | null): 'fee' | 'rate' {
  if (!r || r.fee == null) return 'rate';
  if (r.feeFixed || r.rate == null) return 'fee';
  return Math.round(r.price * r.rate) === Math.round(r.fee) ? 'rate' : 'fee';
}

function RateFormModal({
  mode,
  initial,
  onClose,
}: {
  mode: 'create' | 'edit';
  initial: g2bApi.RateRow | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<string>(initial?.kind ?? '사용자 정의');
  const [vendor, setVendor] = useState<string>(initial?.vendor ?? '');
  const [spec, setSpec] = useState<string>(initial?.spec ?? '');
  const [price, setPrice] = useState<number>(initial?.price ?? 0);
  // 수수료·요율은 둘 중 사람이 적은 쪽이 기준(v2.286.0 · 개발요청 #8).
  //   요율을 적으면 수수료 = 판매금액 × 요율 · 수수료를 적으면 그 금액 그대로, 요율 = 수수료 ÷ 판매금액.
  //   rate 는 표시 시 %, 저장 시 소수. 입력은 % 단위 (예: "5" = 5%). 서버도 같은 규칙으로 다시 맞춘다(routes/g2b/rate-fee.ts).
  const [feeAnchor, setFeeAnchor] = useState<'fee' | 'rate'>(() => initialFeeAnchor(initial));
  const [ratePctRaw, setRatePctRaw] = useState<string>(initial?.rate != null ? ratePctText(initial.rate) : '');
  const [feeRaw, setFeeRaw] = useState<string>(initial?.fee != null ? String(Math.round(initial.fee)) : '');
  const [feeFocused, setFeeFocused] = useState(false);
  const [note, setNote] = useState<string>(initial?.note ?? '');
  const [sortOrder, setSortOrder] = useState<number>(initial?.sortOrder ?? 999);
  // 다량납품할인 기준 (v2.315.0) — 구간마다 「○원(개) 이상 → ○%」 · 비우면 기준 없음
  const [vdBasis, setVdBasis] = useState<VolumeDiscountBasis>(initial?.volumeDiscount?.basis ?? 'amount');
  const [vdRows, setVdRows] = useState<{ min: string; pct: string }[]>(() =>
    (initial?.volumeDiscount?.tiers ?? []).map((t) => ({
      min: initial?.volumeDiscount?.basis === 'qty' ? String(t.min) : t.min.toLocaleString('ko-KR'),
      pct: String(t.pct),
    })));
  const [vdError, setVdError] = useState<string | null>(null);
  const vdParsed = tiersFromText(vdBasis, vdRows);
  const patchVd = (i: number, p: Partial<{ min: string; pct: string }>) => {
    setVdError(null);
    setVdRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  };

  const createMut = useMutation({
    mutationFn: (input: g2bApi.RateInput) => g2bApi.createRate(input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['g2b', 'rate'] }); onClose(); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<g2bApi.RateInput> }) => g2bApi.updateRate(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['g2b', 'rate'] }); onClose(); },
  });

  // 지금 칸 값 → 수수료(원)·요율(소수)
  let feeLive: number | null;
  let rateDecLive: number | null;
  if (feeAnchor === 'fee') {
    feeLive = feeRaw === '' ? null : Number(feeRaw);
    rateDecLive = feeLive != null && (price || 0) > 0 ? feeLive / price : null;
  } else {
    const ratePctLive = ratePctRaw.trim() === '' ? null : Number(ratePctRaw);
    rateDecLive = ratePctLive != null && Number.isFinite(ratePctLive) ? ratePctLive / 100 : null;
    feeLive = rateDecLive != null ? Math.round((price || 0) * rateDecLive) : null;
  }
  // 칸에 보이는 글자 — 계산된 쪽은 요율 둘째 자리 · 수수료 콤마
  const rateShown = feeAnchor === 'rate' ? ratePctRaw : rateDecLive != null ? (rateDecLive * 100).toFixed(2) : '';
  const feeShown = feeAnchor === 'fee'
    ? (feeFocused || feeRaw === '' ? feeRaw : Number(feeRaw).toLocaleString('ko-KR'))
    : feeLive != null ? feeLive.toLocaleString('ko-KR') : '';

  function onSubmit() {
    if (!spec.trim()) { window.alert('규격을 입력하세요'); return; }
    if ('error' in vdParsed) { setVdError(vdParsed.error); return; }
    const input: g2bApi.RateInput = {
      kind: kind.trim() || '사용자 정의',
      vendor: vendor.trim(),
      spec: spec.trim(),
      price: price || 0,
      fee: feeLive,
      rate: rateDecLive,
      // 수수료를 적었으면 그 금액이 기준 — 서버가 요율을 「수수료 ÷ 판매금액」으로 정밀하게 다시 계산한다
      feeFixed: feeAnchor === 'fee' && feeLive != null,
      note: note.trim() || null,
      volumeDiscount: vdParsed.value,
      sortOrder: sortOrder || 999,
    };
    if (mode === 'edit' && initial) updateMut.mutate({ id: initial.id, input });
    else createMut.mutate(input);
  }
  const saving = createMut.isPending || updateMut.isPending;
  const error = createMut.error ?? updateMut.error;

  return (
    <ModalShell title={mode === 'create' ? '정산요율 항목 추가' : '정산요율 항목 수정'} onClose={onClose}>
      <div className="p-6 space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="ui-label">구분</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="ui-input w-full mt-1">
              <option value="MAS">MAS</option>
              <option value="선택부품">선택부품</option>
              <option value="주품목">주품목</option>
              <option value="사용자 정의">사용자 정의</option>
            </select>
          </div>
          <div>
            <label className="ui-label">업체</label>
            <input value={vendor} onChange={(e) => setVendor(e.target.value)} className="ui-input w-full mt-1" placeholder="에스엠시스템즈 / 비알인포텍 …" />
          </div>
        </div>
        <div>
          <label className="ui-label">규격 *</label>
          <textarea value={spec} onChange={(e) => setSpec(e.target.value)} rows={2} className="ui-input ui-textarea w-full mt-1" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="ui-label">판매금액 (원)</label>
            <input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value) || 0)} className="ui-input w-full mt-1 num" />
          </div>
          <div>
            <label className="ui-label">
              요율 (%) {feeAnchor === 'fee' && <span className="text-[11px] text-blue-500" data-rate-tag>수수료로 계산</span>}
            </label>
            <div className="relative mt-1">
              <input
                type="number"
                step="any"
                value={rateShown}
                onChange={(e) => {
                  setFeeAnchor('rate');
                  setRatePctRaw(e.target.value);
                }}
                placeholder="예: 5"
                aria-label="요율 (%)"
                className="ui-input w-full num pr-6"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
            </div>
          </div>
          <div>
            <label className="ui-label">
              수수료 (원){' '}
              {feeAnchor === 'fee'
                ? <span className="text-[11px] text-amber-600" data-fee-tag>직접 입력</span>
                : <span className="text-[11px] text-blue-500" data-fee-tag>요율로 계산</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={feeShown}
              onChange={(e) => {
                setFeeAnchor('fee');
                setFeeRaw(e.target.value.replace(/[^0-9]/g, ''));
              }}
              onFocus={() => setFeeFocused(true)}
              onBlur={() => setFeeFocused(false)}
              placeholder="직접 적거나 요율로 계산"
              aria-label="수수료 (원)"
              className={`ui-input w-full mt-1 num ${feeAnchor === 'fee' ? 'border-amber-300 bg-amber-50/40' : ''}`}
            />
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-2" data-fee-hint>
          {feeAnchor === 'fee'
            ? '적은 수수료가 그대로 저장되고, 요율은 「수수료 ÷ 판매금액」으로 계산됩니다. 판매금액을 바꿔도 수수료는 그대로입니다.'
            : '요율을 적으면 수수료가 「판매금액 × 요율」로 계산됩니다. 수수료를 직접 적으면 그 금액이 그대로 저장됩니다.'}
        </p>
        {/* 다량납품할인 기준 (v2.315.0) — 정산서 작성 창이 그 줄 금액(또는 수량)에 맞는 구간 할인율을 자동으로 채운다 */}
        <div className="border border-rose-200 rounded p-3 bg-rose-50/30" data-volume-discount>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-slate-700">🏷 다량납품할인 기준 <span className="font-normal text-slate-400">(선택)</span></span>
            <SegmentedControl ariaLabel="다량납품할인 기준" value={vdBasis} onChange={(v) => { setVdError(null); setVdBasis(v); }}
              options={[{ key: 'amount', label: '금액 기준' }, { key: 'qty', label: '수량 기준' }]} />
          </div>
          <p className="text-xs text-slate-500 mb-2">
            정산서에 이 항목을 넣으면 <b>{BASIS_LABEL[vdBasis]}</b>이 기준 이상인 구간 중 가장 높은 할인율이 그 줄에 자동으로 채워집니다.
            해당되지 않는 건은 정산서 창에서 그 줄 체크를 끄면 됩니다.
          </p>
          {vdRows.map((row, i) => (
            <div key={i} className="flex items-center gap-1.5 mb-1.5 text-sm">
              <input value={row.min} onChange={(e) => patchVd(i, { min: e.target.value.replace(/[^0-9,]/g, '') })} inputMode="numeric"
                placeholder={vdBasis === 'qty' ? '예: 10' : '예: 50,000,000'} aria-label={`${i + 1}번째 구간 기준`}
                className="ui-input w-40 text-right num" />
              <span className="text-slate-600 w-14">{vdBasis === 'qty' ? '개 이상' : '원 이상'}</span>
              <input type="number" step="any" value={row.pct} onChange={(e) => patchVd(i, { pct: e.target.value })}
                placeholder="예: 0.5" aria-label={`${i + 1}번째 구간 할인율 %`} className="ui-input w-20 text-right num" />
              <span className="text-slate-600">%</span>
              <button type="button" onClick={() => { setVdError(null); setVdRows((prev) => prev.filter((_, idx) => idx !== i)); }}
                title="이 구간 지우기" className="btn btn-secondary btn-sm">🗑</button>
            </div>
          ))}
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => setVdRows((prev) => [...prev, { min: '', pct: '' }])} disabled={vdRows.length >= 10}
              className="btn btn-secondary btn-sm">+ 구간 추가</button>
            {'value' in vdParsed && vdParsed.value && <span className="text-xs text-rose-700">→ {volumeDiscountLabel(vdParsed.value)}</span>}
            {'value' in vdParsed && !vdParsed.value && <span className="text-xs text-slate-400">기준 없음 — 정산서에서 자동으로 채우지 않습니다</span>}
          </div>
          {vdError && <div className="mt-1.5 text-xs text-red-700">{vdError}</div>}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="ui-label">비고</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="ui-input w-full mt-1" />
          </div>
          <div>
            <label className="ui-label">정렬 순서</label>
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 999)} className="ui-input w-full mt-1 num" />
          </div>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">{(error as Error).message}</div>
        )}
      </div>
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
        <button type="button" onClick={onClose} className="btn btn-secondary">취소</button>
        <button type="button" onClick={onSubmit} disabled={saving}
          className="btn btn-primary">
          {saving ? '저장 중…' : mode === 'create' ? '추가' : '수정'}
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================
// 4) BOM 원가 산정 (MAS only)
// =========================================================
// BOM 선택 대상(정규화) — MAS 또는 3자단가
interface BomSel {
  kind: 'mas' | 'tpp';
  key: string;        // BOM 저장 키 (MAS: 풀네임||가격, tpp: id)
  name: string;       // 규격(전체명)
  label: string;      // 짧은 표시명
  g2bId: string | null;
  category: string;
  price: number;
  deliveryDays: string;
}

function BomTab() {
  const [sub, setSub] = useState<'mas' | 'tpp'>('mas');
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [selTppId, setSelTppId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const itemSpecLookup = useItemSpecLookup();
  const queryClient = useQueryClient();

  // MAS 제품(등록현황과 동일 DB — 2026-07-19 수리: 하드코딩 배열 대신 DB 사용, 등록현황 수정이 BOM 에도 반영)
  // + 3자단가 목록(현황과 자동 동기화) + 정산요율(영업수수료 자동완성용) + BOM 구성(DB 영구저장)
  const masQuery = useQuery({ queryKey: ['g2b', 'mas'], queryFn: g2bApi.listMas, staleTime: 60_000 });
  const tppQuery = useQuery({ queryKey: ['g2b', 'tpp'], queryFn: g2bApi.listTpp, staleTime: 60_000 });
  const rateQuery = useQuery({ queryKey: ['g2b', 'rate'], queryFn: g2bApi.listRate, staleTime: 60_000 });
  const bomQuery = useQuery({ queryKey: ['g2b', 'bom'], queryFn: g2bApi.listBom, staleTime: 30_000 });
  const masRows = masQuery.data ?? [];
  const tppRows = tppQuery.data ?? [];

  // DB 구성 → 화면용 맵(MAS: 인덱스, tpp: id). 저장키로 매칭.
  const bomRows = bomQuery.data ?? [];
  // 방어: 혹시라도 items 가 배열이 아니면(과거 이중인코딩 잔재 등) 빈 배열로 취급 — 화면 크래시 방지.
  const asItems = (v: unknown): BomItem[] => (Array.isArray(v) ? (v as BomItem[]) : []);
  const templates = useMemo(() => {
    const map: Record<number, BomItem[]> = {};
    for (const r of bomRows) {
      if (r.targetKind !== 'mas') continue;
      const idx = masRows.findIndex((m) => masKey(m) === r.targetKey);
      if (idx >= 0) map[idx] = asItems(r.items);
    }
    return map;
  }, [bomRows, masRows]);
  const tppTemplates = useMemo(() => {
    const map: Record<number, BomItem[]> = {};
    for (const r of bomRows) {
      if (r.targetKind !== 'tpp') continue;
      const id = Number(r.targetKey);
      if (!Number.isNaN(id)) map[id] = asItems(r.items);
    }
    return map;
  }, [bomRows]);

  // 저장 mutation — 낙관적 업데이트로 즉시 반영 후 서버 저장.
  // 성공 시엔 재조회하지 않는다(낙관적 캐시가 이미 저장한 값과 동일 → 재조회 경쟁 방지).
  // 실패 시엔 사용자에게 알리고 서버 기준으로 재동기화(낙관적 변경 되돌림) — 조용한 유실 방지.
  const saveMut = useMutation({
    mutationFn: (v: { targetKind: 'mas' | 'tpp'; targetKey: string; items: BomItem[] }) =>
      g2bApi.saveBom(v.targetKind, v.targetKey, v.items),
    onError: () => {
      alert('구성 저장에 실패했습니다. 네트워크 상태를 확인하고 다시 시도해 주세요.');
      queryClient.invalidateQueries({ queryKey: ['g2b', 'bom'] });
    },
  });

  // 인라인 편집(요율·기준)이 키 입력마다 저장 요청을 쏘지 않도록 대상별로 저장을 디바운스.
  // 저장은 항상 "그 시점의 전체 items" 를 upsert 하므로 마지막 것만 반영되면 충분(경쟁 방지).
  const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingSaves = useRef<Map<string, { targetKind: 'mas' | 'tpp'; targetKey: string; items: BomItem[] }>>(new Map());
  useEffect(() => {
    const timers = saveTimers.current;
    const pending = pendingSaves.current;
    return () => {
      // 언마운트/페이지 이동 시 대기 중인 저장을 즉시 발사(유실 방지).
      for (const t of timers.values()) clearTimeout(t);
      for (const v of pending.values()) g2bApi.saveBom(v.targetKind, v.targetKey, v.items).catch(() => {});
      timers.clear();
      pending.clear();
    };
  }, []);

  // 정규화된 선택 대상
  const sel: BomSel | null = useMemo(() => {
    if (sub === 'mas') {
      const m = masRows[selectedIdx];
      if (!m) return null;
      return { kind: 'mas', key: masKey(m), name: m.fullName, label: masModel(m), g2bId: m.g2bId, category: m.category, price: masPrice(m), deliveryDays: m.deliveryDays };
    }
    const t = tppRows.find((x) => x.id === selTppId);
    if (!t) return null;
    return { kind: 'tpp', key: String(t.id), name: t.specText, label: (t.specText.split(',')[0] || t.cat).trim(), g2bId: t.g2bId, category: t.cat, price: t.price, deliveryDays: '30일' };
  }, [sub, selectedIdx, selTppId, tppRows, masRows]);

  const productPrice = sel?.price ?? 0;
  const bomItems = sub === 'mas' ? (templates[selectedIdx] ?? []) : (selTppId != null ? (tppTemplates[selTppId] ?? []) : []);
  const hasBom = bomItems.length > 0;

  // 등록 판매가(productPrice)는 나라장터 등록가 = 부가세 포함가.
  // BOM 매입원가는 매입단가 = 공급가(부가세 별도)라 기준이 다르므로,
  // 마진은 공급가 기준으로 통일한다 — 판매가를 부가세 제외(÷1.1)한 공급가와 원가를 비교.
  // (영업수수료 행은 정산요율이 부가세 포함가 기준이라 bomLineCost 에서 productPrice 그대로 사용.)
  const supplyPrice = Math.round(productPrice / 1.1); // 판매 공급가 (부가세 제외)
  const totalCost = bomItems.reduce((a, i) => a + bomLineCost(i, productPrice), 0);
  const margin = supplyPrice - totalCost;
  const marginRate = supplyPrice > 0 && hasBom ? ((margin / supplyPrice) * 100).toFixed(1) : '-';

  // 선택 제품에 해당하는 정산요율(=영업수수료 자동완성 요율%)
  const autoCommissionPct = useMemo(() => {
    if (!sel) return 0;
    const r = (rateQuery.data ?? []).find((x) => x.spec.trim() === sel.name.trim());
    // 깎지 않고 가져온다(v2.286.0) — 수수료를 직접 적은 항목은 요율이 25.3846153846% 같은 정밀 값이다
    return r?.rate != null ? +(r.rate * 100).toFixed(8) : 0;
  }, [rateQuery.data, sel]);
  const hasCommission = bomItems.some((i) => i.kind === 'commission');

  // 현재 선택 제품의 구성 저장 — 낙관적 캐시 즉시 반영 + 서버 저장(디바운스). (빈 구성이면 서버가 행 삭제)
  const setBomForCurrent = (next: BomItem[]) => {
    if (!sel) return;
    const { kind, key } = sel;
    // 낙관적 캐시 즉시 반영 (화면 즉시 갱신)
    queryClient.setQueryData<g2bApi.BomTemplateRow[]>(['g2b', 'bom'], (old) => {
      const list = (old ?? []).filter((r) => !(r.targetKind === kind && r.targetKey === key));
      if (next.length > 0) list.push({ targetKind: kind, targetKey: key, items: next });
      return list;
    });
    // 서버 저장은 대상별 디바운스 — 연속 입력을 한 번의 upsert 로 합쳐 경쟁/과다요청 방지.
    const payload = { targetKind: kind, targetKey: key, items: next };
    pendingSaves.current.set(key, payload);
    const prev = saveTimers.current.get(key);
    if (prev) clearTimeout(prev);
    saveTimers.current.set(key, setTimeout(() => {
      saveTimers.current.delete(key);
      pendingSaves.current.delete(key);
      saveMut.mutate(payload);
    }, 400));
  };

  // ── 최근 매입가 갱신 — 같은 부품들의 최근/평균 매입가를 매입내역에서 다시 불러와 반영·저장 ──
  const refreshCosts = async () => {
    if (bomItems.length === 0 || refreshing) return;
    setRefreshing(true);
    try {
      const results = await Promise.all(
        bomItems.map((it) => (it.kind === 'commission' ? Promise.resolve(null) : fetchRecentSupplier(it.itemCode).catch(() => null))),
      );
      const next = bomItems.map((it, idx) => {
        const info = results[idx];
        if (!info) return it;
        return {
          ...it,
          recent: info.recentPrice || it.recent,
          avg: info.avgPrice || it.avg,
          vendor: it.vendor && it.vendor !== '—' ? it.vendor : (info.supplier || it.vendor),
        };
      });
      setBomForCurrent(next);
    } finally {
      setRefreshing(false);
    }
  };

  const filteredMas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return masRows.map((m, idx) => ({ m, idx })).filter(({ m }) => !q || `${m.fullName} ${m.g2bId ?? ''}`.toLowerCase().includes(q));
  }, [search, masRows]);
  const filteredTpp = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tppRows.filter((t) => !q || `${t.specText} ${t.cat} ${t.g2bId}`.toLowerCase().includes(q));
  }, [tppRows, search]);

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* 좌측: 제품 리스트 (MAS / 3자단가) */}
      <aside className="col-span-4">
        <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="ui-subsection-title">1. 제품 선택</h3>
          </div>
          {/* MAS / 3자단가 서브탭 */}
          <SegmentedControl ariaLabel="BOM 계약유형" className="mb-3" value={sub} onChange={setSub}
            options={[{ key: 'mas', label: 'MAS' }, { key: 'tpp', label: '3자단가' }]} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ui-input w-full mb-3"
            placeholder="🔍 모델 / 규격 / 식별번호"
          />
          <div className="max-h-[600px] overflow-y-auto pr-1">
            {sub === 'mas' && filteredMas.map(({ m, idx }) => {
              const tplCount = (templates[idx] ?? []).length;
              const isActive = idx === selectedIdx;
              return (
                <button key={idx} type="button" onClick={() => setSelectedIdx(idx)}
                  className={`w-full text-left text-xs border rounded p-2 mb-1 transition-colors ${isActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${m.category === '서버' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{m.category}</span>
                    {tplCount > 0 ? <span className="text-[11px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">BOM {tplCount}</span> : <span className="text-[11px] text-slate-500">미등록</span>}
                  </div>
                  <div className="mt-1 font-semibold">{masModel(m)}</div>
                  <div className="text-slate-400 mt-0.5">{m.priceText}</div>
                </button>
              );
            })}
            {sub === 'tpp' && filteredTpp.map((t) => {
              const tplCount = (tppTemplates[t.id] ?? []).length;
              const isActive = t.id === selTppId;
              return (
                <button key={t.id} type="button" onClick={() => setSelTppId(t.id)}
                  className={`w-full text-left text-xs border rounded p-2 mb-1 transition-colors ${isActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{t.ctype}</span>
                    {tplCount > 0 ? <span className="text-[11px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">BOM {tplCount}</span> : <span className="text-[11px] text-slate-500">미등록</span>}
                  </div>
                  <div className="mt-1 font-semibold truncate">{(t.specText.split(',')[0] || t.cat).trim()}</div>
                  <div className="text-slate-400 mt-0.5">{fmt(t.price)} · {t.g2bId}</div>
                </button>
              );
            })}
            {sub === 'mas' && filteredMas.length === 0 && <div className="text-center text-xs text-slate-500 py-8">검색 결과 없음</div>}
            {sub === 'tpp' && (tppQuery.isLoading ? <div className="text-center text-xs text-slate-500 py-8"><LoadingLines /></div> : filteredTpp.length === 0 && <div className="text-center text-xs text-slate-500 py-8">검색 결과 없음</div>)}
          </div>
        </div>
      </aside>

      {/* 우측: BOM */}
      <main className="col-span-8 space-y-4">
        {!sel ? (
          <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-slate-400 text-sm">
            좌측에서 제품을 선택하세요
          </div>
        ) : (
        <>
        {/* 선택된 제품 헤더 */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-blue-700">선택된 {sel.kind === 'mas' ? 'MAS' : '3자단가'} 제품</div>
              <div className="font-bold text-blue-900 mt-1">{sel.name}</div>
              <div className="text-xs text-blue-600 mt-1">
                {sel.g2bId ? `식별번호 ${sel.g2bId}` : '식별번호 미확인'} · {sel.category} · 납품 {sel.deliveryDays}
              </div>
            </div>
            <div className="text-right whitespace-nowrap">
              <div className="text-xs text-blue-700">등록 판매가 <span className="text-[11px] text-blue-500">부가세 포함</span></div>
              <div className="text-2xl font-bold num text-blue-900">{fmt(productPrice)}</div>
              <div className="text-xs text-blue-600 mt-0.5">공급가 {fmt(supplyPrice)} <span className="text-blue-400">(부가세 제외)</span></div>
            </div>
          </div>
        </div>

        {/* BOM 표 */}
        <div className="bg-white rounded shadow-sm border border-slate-200">
          <div className="flex justify-between items-center p-4 border-b border-slate-200">
            <div>
              <h3 className="ui-subsection-title">2. 구성 부품 (BOM)</h3>
              <p className="text-xs text-slate-500 mt-0.5">구성은 자동 저장됩니다 · 매입가는 [최근 매입가 갱신]으로 최신화 · 영업수수료는 정산요율에서 자동완성</p>
            </div>
            <div className="flex items-center gap-2">
              {hasBom && (
                <button
                  type="button"
                  onClick={refreshCosts}
                  disabled={refreshing}
                  title="같은 부품들의 최근/평균 매입가를 매입내역에서 다시 불러와 반영합니다"
                  className={`btn btn-sm ${refreshing ? 'btn-secondary' : 'btn-success'}`}
                >
                  {refreshing ? '갱신 중…' : '🔄 최근 매입가 갱신'}
                </button>
              )}
              <button type="button" onClick={() => setEditing(true)} className="btn btn-primary btn-sm">✏️ 구성항목 수정</button>
            </div>
          </div>

          {!hasBom ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              <div className="text-3xl mb-2">📦</div>
              <div>이 제품은 아직 구성항목이 등록되지 않았습니다.</div>
              <button type="button" onClick={() => setEditing(true)} className="btn btn-primary btn-sm mt-3">+ 구성항목 등록</button>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr className="bg-slate-50 text-slate-600">
                  <th className="px-2 py-1.5 text-center w-8">#</th>
                  <th className="px-2 py-1.5 text-left">품목명</th>
                  <th className="px-2 py-1.5 text-left">규격</th>
                  <th className="px-2 py-1.5 text-left w-24">매입처</th>
                  <th className="px-2 py-1.5 text-right w-12">수량</th>
                  <th className="px-2 py-1.5 text-right w-32">최근매입가</th>
                  <th className="px-2 py-1.5 text-right w-32">평균매입가</th>
                  <th className="px-2 py-1.5 text-center w-20">기준</th>
                  <th className="px-2 py-1.5 text-right w-32">합계</th>
                </tr>
              </thead>
              <tbody>
                {bomItems.map((it, idx) => {
                  if (it.kind === 'commission') {
                    const cost = bomLineCost(it, productPrice);
                    return (
                      <tr key={`comm-${idx}`} className="bg-amber-50/50">
                        <td className="text-center px-2 py-2">{idx + 1}</td>
                        <td className="px-2 py-2 font-medium">🧾 {it.itemName}</td>
                        <td className="px-2 py-2 text-slate-500" colSpan={5}>
                          판매가 {fmt(productPrice)} × 요율
                          <PctInput value={it.ratePct ?? 0} ariaLabel="영업수수료 요율 %"
                            onChange={(v) => setBomForCurrent(bomItems.map((x, i) => i === idx ? { ...x, ratePct: Number(v) || 0 } : x))}
                            className="ui-input ui-input-sm w-16 mx-1 text-right num" />%
                        </td>
                        <td className="px-2 py-2 text-center text-slate-400">—</td>
                        <td className="px-2 py-2 text-right num font-semibold">{fmt(cost)}</td>
                      </tr>
                    );
                  }
                  const applied = it.basis === 'recent' ? it.recent : it.avg;
                  const spec = it.spec || itemSpecLookup.get(it.itemCode)?.spec || '';
                  return (
                    <tr key={`${it.itemCode}-${idx}`}>
                      <td className="text-center px-2 py-2">{idx + 1}</td>
                      <td className="px-2 py-2 font-medium">{it.itemName}</td>
                      <td className="px-2 py-2 text-slate-600">{spec || <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2 text-slate-600">{it.vendor}</td>
                      <td className="px-2 py-2 text-right">{it.qty}</td>
                      <td className={`px-2 py-2 text-right num ${it.basis === 'recent' ? 'font-bold text-blue-700' : 'text-slate-500'}`}>{fmt(it.recent)}</td>
                      <td className={`px-2 py-2 text-right num ${it.basis === 'avg' ? 'font-bold text-blue-700' : 'text-slate-500'}`}>{fmt(it.avg)}</td>
                      <td className="px-2 py-2 text-center">
                        <select value={it.basis}
                          onChange={(e) => setBomForCurrent(bomItems.map((x, i) => i === idx ? { ...x, basis: e.target.value as 'recent' | 'avg' } : x))}
                          className="ui-input ui-input-sm">
                          <option value="recent">최근</option>
                          <option value="avg">평균</option>
                        </select>
                      </td>
                      <td className="px-2 py-2 text-right num font-semibold">{fmt(it.qty * applied)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 마진 요약 */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
            <div className="text-xs text-slate-500">판매 공급가 <span className="text-[11px] text-slate-500">부가세 제외</span></div>
            <div className="text-xl font-bold num mt-1">{fmt(supplyPrice)}</div>
            <div className="text-xs text-slate-500 mt-0.5">등록가 {fmt(productPrice)} (VAT포함)</div>
          </div>
          <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
            <div className="text-xs text-slate-500">BOM 총 원가 <span className="text-[11px] text-slate-500">부가세 제외</span></div>
            <div className="text-xl font-bold num mt-1">{hasBom ? fmt(totalCost) : '-'}</div>
          </div>
          <div className={`rounded p-4 border ${hasBom ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className={`text-xs ${hasBom ? 'text-emerald-700' : 'text-slate-500'}`}>마진 <span className="text-[11px] font-normal text-emerald-500">공급가 기준</span></div>
            <div className={`text-xl font-bold num mt-1 ${!hasBom ? 'text-slate-400' : margin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{hasBom ? fmt(margin) : '-'}</div>
          </div>
          <div className={`rounded p-4 border ${hasBom ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className={`text-xs ${hasBom ? 'text-emerald-700' : 'text-slate-500'}`}>마진율 <span className="text-[11px] font-normal text-emerald-500">공급가 기준</span></div>
            <div className={`text-xl font-bold num mt-1 ${!hasBom ? 'text-slate-400' : margin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{marginRate}{marginRate === '-' ? '' : '%'}</div>
          </div>
        </div>
        </>
        )}
      </main>

      {editing && sel && (
        <BomEditModal
          productLabel={sel.label}
          items={bomItems}
          productPrice={productPrice}
          autoCommissionPct={autoCommissionPct}
          hasCommission={hasCommission}
          onClose={() => setEditing(false)}
          onSave={(next) => { setBomForCurrent(next); setEditing(false); }}
        />
      )}
    </div>
  );
}

// ---------- BOM 구성항목 수정 모달 ----------
function BomEditModal({
  productLabel,
  items,
  productPrice,
  autoCommissionPct,
  onClose,
  onSave,
}: {
  productLabel: string;
  items: BomItem[];
  productPrice: number;
  autoCommissionPct: number;
  hasCommission: boolean;
  onClose: () => void;
  onSave: (next: BomItem[]) => void;
}) {
  const [draft, setDraft] = useState<BomItem[]>(items);
  const itemSpecLookup = useItemSpecLookup();
  // 신규 행 입력 상태 — 콤보박스에서 선택된 품목 + 자동 fetch 결과
  const [picked, setPicked] = useState<ItemMasterRow | null>(null);
  const [pickedSupplier, setPickedSupplier] = useState<string>('');
  const [pickedRecent, setPickedRecent] = useState<number>(0);
  const [pickedAvg, setPickedAvg] = useState<number>(0);
  const [qtyInput, setQtyInput] = useState<string>('1');

  const removeAt = (idx: number) => setDraft(draft.filter((_, i) => i !== idx));
  const updateAt = (idx: number, patch: Partial<BomItem>) =>
    setDraft(draft.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const draftHasCommission = draft.some((i) => i.kind === 'commission');
  const addCommission = () => {
    if (draftHasCommission) return;
    setDraft([
      ...draft,
      { itemCode: COMMISSION_CODE, itemName: '영업수수료', vendor: '—', qty: 1, recent: 0, avg: 0, basis: 'recent', kind: 'commission', ratePct: autoCommissionPct },
    ]);
  };

  const addItem = () => {
    if (!picked) return;
    const qty = parseInt(qtyInput, 10) || 1;
    setDraft([
      ...draft,
      {
        itemCode: picked.itemCode,
        itemName: picked.itemName ?? '',
        spec: picked.spec ?? '',
        vendor: pickedSupplier.trim() || '—',
        qty,
        recent: pickedRecent,
        avg: pickedAvg,
        basis: 'recent',
      },
    ]);
    setPicked(null);
    setPickedSupplier('');
    setPickedRecent(0);
    setPickedAvg(0);
    setQtyInput('1');
  };

  return (
    <ModalShell title={`구성항목 수정 — ${productLabel}`} onClose={onClose} width={840}>
      <div className="p-6 space-y-4">
        <p className="text-xs text-slate-500">
          이 제품을 만들기 위한 부품 목록을 정의합니다. 확인을 누르면 자동 저장됩니다. 매입가(최근/평균)는 품목 추가 시 매입내역에서 자동으로 채워지며, 이후 [최근 매입가 갱신]으로 최신화할 수 있습니다.
        </p>

        {/* 현재 구성 */}
        <div className="border border-slate-200 rounded overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 border-b border-slate-200">
            현재 구성 ({draft.length}건)
          </div>
          {draft.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-6">아직 등록된 항목이 없습니다</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="bg-white text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-1.5 text-left">품목명</th>
                  <th className="px-2 py-1.5 text-left">규격</th>
                  <th className="px-2 py-1.5 text-left w-32">매입처</th>
                  <th className="px-2 py-1.5 text-right w-20">수량</th>
                  <th className="px-2 py-1.5 text-center w-12"></th>
                </tr>
              </thead>
              <tbody>
                {draft.map((it, idx) => {
                  if (it.kind === 'commission') {
                    const amt = Math.round(productPrice * ((it.ratePct ?? 0) / 100));
                    return (
                      <tr key={`comm-${idx}`} className="border-b border-slate-100 bg-amber-50/50">
                        <td className="px-2 py-1.5 font-medium">🧾 {it.itemName}</td>
                        <td className="px-2 py-1.5 text-slate-500">판매가 {fmt(productPrice)} × 요율(정산요율 자동완성)</td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <PctInput value={it.ratePct ?? 0} ariaLabel="영업수수료 요율 %"
                              onChange={(v) => updateAt(idx, { ratePct: Number(v) || 0 })}
                              className="ui-input ui-input-sm w-16 text-right num" />
                            <span className="text-slate-500">%</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right num font-semibold">{fmt(amt)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <button type="button" onClick={() => removeAt(idx)} title="삭제" className="btn btn-ghost-danger btn-sm">🗑</button>
                        </td>
                      </tr>
                    );
                  }
                  const spec = it.spec || itemSpecLookup.get(it.itemCode)?.spec || '';
                  return (
                    <tr key={`${it.itemCode}-${idx}`} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 font-medium">{it.itemName}</td>
                      <td className="px-2 py-1.5 text-slate-600">{spec || <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-1.5">
                        <input
                          value={it.vendor}
                          onChange={(e) => updateAt(idx, { vendor: e.target.value })}
                          className="ui-input ui-input-sm w-full"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input
                          type="number"
                          value={it.qty}
                          onChange={(e) => updateAt(idx, { qty: parseInt(e.target.value, 10) || 0 })}
                          className="ui-input ui-input-sm w-16 text-right"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={() => removeAt(idx)}
                          title="삭제"
                          className="btn btn-ghost-danger btn-sm"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 영업수수료 추가 */}
        <div className="border border-amber-200 rounded p-3 bg-amber-50/40 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-600">
            🧾 <span className="font-semibold">영업수수료</span> — 정산요율의 요율({+autoCommissionPct.toFixed(2)}%)로 자동완성, 표에서 요율% 수정 가능
          </div>
          <button
            type="button"
            onClick={addCommission}
            disabled={draftHasCommission}
            className={`btn btn-sm ${draftHasCommission ? 'btn-secondary' : 'btn-warning-solid'}`}
          >
            {draftHasCommission ? '추가됨' : '+ 영업수수료 추가'}
          </button>
        </div>

        {/* 신규 행 */}
        <div className="border border-blue-200 rounded p-3 bg-blue-50/30 space-y-2">
          <div className="text-xs font-semibold text-slate-600">+ 새 항목 추가</div>
          <ItemPickerCombobox
            placeholder="🔍 품명·품목그룹·규격으로 검색"
            onPick={async (row) => {
              setPicked(row);
              try {
                const info = await fetchRecentSupplier(row.itemCode);
                setPickedSupplier(info.supplier);
                setPickedRecent(info.recentPrice);
                setPickedAvg(info.avgPrice);
              } catch {
                setPickedSupplier('');
                setPickedRecent(0);
                setPickedAvg(0);
              }
            }}
            value={picked}
            onClear={() => { setPicked(null); setPickedSupplier(''); setPickedRecent(0); setPickedAvg(0); }}
          />
          {picked && (
            <div className="grid grid-cols-12 gap-2 text-xs items-center">
              <div className="col-span-2 text-slate-500">매입처</div>
              <input
                value={pickedSupplier}
                onChange={(e) => setPickedSupplier(e.target.value)}
                placeholder="매입내역에서 자동 — 수정 가능"
                className="ui-input col-span-5"
              />
              <div className="col-span-1 text-slate-500 text-right">수량</div>
              <input
                type="number"
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                className="ui-input col-span-2 text-right"
              />
              <button
                type="button"
                onClick={addItem}
                className="btn btn-primary col-span-2"
              >
                + 추가
              </button>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-500">
          💡 품목은 [재고관리 ▸ 품명&품목] 마스터에서 검색해 가져옵니다. 매입처는 매입내역에서 자동 fetch (수정 가능).
        </p>
      </div>

      <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          취소
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          className="btn btn-primary"
        >
          확인
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================
// 품명&품목 마스터 캐시 + itemCode → 행 lookup map
// (BomTab / BomEditModal 공용 — react-query 가 같은 key 로 캐시 공유)
// =========================================================
function useItemSpecLookup(): Map<string, ItemMasterRow> {
  const query = useQuery({
    queryKey: ['stock', 'items', 'all-for-picker'],
    queryFn: () => listItems(),
    staleTime: 5 * 60_000,
  });
  return useMemo(() => {
    const m = new Map<string, ItemMasterRow>();
    for (const r of query.data ?? []) m.set(r.itemCode, r);
    return m;
  }, [query.data]);
}

// =========================================================
// 품목 검색 콤보박스 — 품명·품목그룹·규격으로 검색 (P/N 제외)
// =========================================================
function ItemPickerCombobox({
  placeholder,
  onPick,
  value,
  onClear,
}: {
  placeholder: string;
  onPick: (row: ItemMasterRow) => void;
  value: ItemMasterRow | null;
  onClear: () => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // 전체 품목 마스터 — 한 번 받아서 캐시
  const query = useQuery({
    queryKey: ['stock', 'items', 'all-for-picker'],
    queryFn: () => listItems(),
    staleTime: 5 * 60_000,
  });
  const allRows = query.data ?? [];

  // 클라이언트 사이드 필터 — 품명·품목그룹·규격 부분일치 (대소문자 무시, P/N 제외)
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return allRows
      .filter((r) => {
        const name = (r.itemName ?? '').toLowerCase();
        const group = (r.itemGroup ?? '').toLowerCase();
        const spec = (r.spec ?? '').toLowerCase();
        return name.includes(term) || group.includes(term) || spec.includes(term);
      })
      .slice(0, 20);
  }, [allRows, q]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // 선택된 값이 있으면 칩으로 표시
  if (value) {
    return (
      <div className="flex items-center gap-2 text-xs bg-white border border-blue-300 rounded px-2 py-1.5">
        <span className="font-semibold text-slate-700">{value.itemName}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-500">{value.spec ?? '-'}</span>
        <span className="text-slate-400">·</span>
        <span className="font-mono text-slate-400">{value.itemCode}</span>
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-slate-400 hover:text-red-500"
          title="다시 선택"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="ui-input ui-input-sm w-full"
      />
      {open && q.trim() && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-300 rounded shadow-lg max-h-72 overflow-y-auto">
          {query.isLoading ? (
            <div className="px-3 py-2 text-xs text-slate-500"><LoadingLines /></div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">검색 결과 없음</div>
          ) : (
            filtered.map((r) => (
              <button
                key={r.itemCode}
                type="button"
                onClick={() => { onPick(r); setOpen(false); setQ(''); }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b border-slate-100"
              >
                <div className="flex items-baseline gap-1.5">
                  <span className="font-semibold text-slate-800">{r.itemName}</span>
                  {r.itemGroup && (
                    <span className="text-[11px] px-1 py-0.5 bg-slate-100 text-slate-600 rounded">
                      {r.itemGroup}
                    </span>
                  )}
                </div>
                <div className="text-slate-500 mt-0.5">
                  {r.spec ? <span>{r.spec}</span> : <span className="text-slate-300">규격 -</span>}
                  <span className="font-mono text-slate-400 ml-2">{r.itemCode}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// =========================================================
// 공통 모달 셸
// =========================================================
function ModalShell({
  title,
  onClose,
  width = 680,
  children,
}: {
  title: string;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div
        style={{ width, maxWidth: '95vw' }}
        className="bg-white rounded shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="ui-modal-title">{title}</h2>
          <button type="button" onClick={onClose} className="ui-close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

